import {useState} from 'react';
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody} from '@/components/ui/dialog.tsx';
import {Button} from '@/components/ui/button.tsx';
import {Label} from '@/components/ui/label.tsx';
import {Spinner} from '@/components/ui/spinner.tsx';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select.tsx';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs.tsx';
import {toast} from 'sonner';
import {AddCreditCard} from '@/components/add-credit-card.tsx';
import {CustomerDetails, useUpdateCustomerBillingMutation} from '@/store/customerFile.ts';
import {PhoneOutgoingIcon, WrenchIcon} from 'lucide-react';
import {NewServiceCallModal} from '@/pages/service-request/components/NewServiceCallModal.tsx';

const BILLING_DAYS = [
    {value: '5', label: 'ה-5 לחודש'},
    {value: '10', label: 'ה-10 לחודש'},
    {value: '15', label: 'ה-15 לחודש'},
    {value: '20', label: 'ה-20 לחודש'},
];

interface Props {
    open: boolean;
    onClose: () => void;
    customer: CustomerDetails;
}

export function EditBillingModal({open, onClose, customer}: Props) {
    const [updateBilling, {isLoading: isSavingDay}] = useUpdateCustomerBillingMutation();
    const [selectedDay, setSelectedDay] = useState(String(customer.payment_day));

    async function handleSaveBillingDay() {
        try {
            await updateBilling({payment_day: Number(selectedDay)}).unwrap();
            toast.success('יום החיוב עודכן בהצלחה');
        } catch {
            toast.error('שגיאה בעדכון יום החיוב');
        }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>עריכת פרטי חיוב ותשלום</DialogTitle>
                </DialogHeader>
                <DialogBody>
                <Tabs defaultValue="billing-day" dir="rtl" className="pt-1">
                    <TabsList className="w-full">
                        <TabsTrigger value="billing-day" className="flex-1">יום חיוב</TabsTrigger>
                        <TabsTrigger value="payment-method" className="flex-1">אמצעי תשלום</TabsTrigger>
                    </TabsList>

                    <TabsContent value="billing-day" className="space-y-4 pt-4">
                        <p className="text-sm text-gray-500">
                            בחר את היום בחודש שבו יתבצע החיוב החודשי.
                        </p>
                        <div className="space-y-1.5">
                            <Label>יום חיוב</Label>
                            <Select value={selectedDay} onValueChange={setSelectedDay}>
                                <SelectTrigger>
                                    <SelectValue placeholder="בחר יום" />
                                </SelectTrigger>
                                <SelectContent>
                                    {BILLING_DAYS.map((d) => (
                                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={onClose} disabled={isSavingDay}>ביטול</Button>
                            <Button
                                onClick={handleSaveBillingDay}
                                disabled={isSavingDay || selectedDay === String(customer.payment_day)}
                            >
                                {isSavingDay && <Spinner size="sm" />}
                                שמור
                            </Button>
                        </div>
                    </TabsContent>

                    <TabsContent value="payment-method" className="pt-4">
                        <PaymentMethodSection customer={customer} onClose={onClose} />
                    </TabsContent>
                </Tabs>
                </DialogBody>
            </DialogContent>
        </Dialog>
    );
}

function PaymentMethodSection({customer, onClose}: {customer: CustomerDetails; onClose: () => void}) {
    const [mode, setMode] = useState<'credit' | 'other'>(
        customer.payment_type === 1 || customer.payment_type === 7 ? 'credit' : 'other'
    );
    const [newCallOpen, setNewCallOpen] = useState(false);

    return (
        <>
        <div className="space-y-4">
            <div className="flex gap-3">
                <button
                    type="button"
                    onClick={() => setMode('credit')}
                    className={`flex-1 border rounded-xl p-3 text-sm transition text-center ${
                        mode === 'credit'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                >
                    כרטיס אשראי
                </button>
                <button
                    type="button"
                    onClick={() => setMode('other')}
                    className={`flex-1 border rounded-xl p-3 text-sm transition text-center ${
                        mode === 'other'
                            ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                >
                    אמצעי תשלום אחר
                </button>
            </div>

            {mode === 'credit' && (
                <div>
                    <AddCreditCard
                        id={customer.id}
                        onSuccess={() => {
                            onClose();
                        }}
                    />
                </div>
            )}

            {mode === 'other' && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 space-y-3 text-sm text-gray-600">
                    <div className="flex items-start gap-2">
                        <WrenchIcon className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
                        <p>
                            לשינוי אמצעי תשלום לאמצעי שאינו כרטיס אשראי (כגון הוראת קבע), יש לפנות לצוות הנהלת החשבונות שלנו.
                        </p>
                    </div>
                    <Button variant="outline" className="w-full mt-1" onClick={() => { onClose(); setNewCallOpen(true); }}>
                        <PhoneOutgoingIcon className="w-4 h-4" />
                        שלח פניה לצוות הנה"ח
                    </Button>
                </div>
            )}
        </div>

        <NewServiceCallModal
            open={newCallOpen}
            onOpenChange={setNewCallOpen}
            defaultDepartment="36"
        />
        </>
    );
}
